import { createAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import * as itemTypes from '../../../items/item-types';
import {
  getItem as getItemInternal,
  saveItem as saveItemInternal
} from '../../../items/item.actions';
import {
  DEFAULT_FEE_ENTRY,
  DEFAULT_REVENUE_SPLIT_SECTION,
  GUARANTEE_TYPE,
  getGuaranteeTierIssues,
  hasValue,
  isRevshareTier,
  validateRevSharePercentage
} from './contract-terms-utils';

export const receiveContractTerms = createAction('receiveContractTerms');
export const setContractTermsIsBusy = createAction('setContractTermsIsBusy');
export const modifyContractTerms = createAction('modifyContractTerms');
export const closeContractTerms = createAction('closeContractTerms');

const contractTermsModel = (id, name) => ({
  id,
  name,
  type: itemTypes.CONTRACT_TERMS,
  fixedFees: [],
  revenueSplits: [
    {
      id: uuidv4(),
      ...DEFAULT_REVENUE_SPLIT_SECTION
    }
  ],
  referralFees: []
});

export const getItem = getItemInternal(
  contractTermsModel,
  receiveContractTerms,
  setContractTermsIsBusy
);

// Helper function to validate a date is within contract term range
const validateDateInRange = (
  date,
  contractStart,
  contractEnd,
  prefix,
  fieldName
) => {
  if (date < contractStart) {
    return `${prefix}: ${fieldName} cannot be before Contract Term Start Date`;
  }
  if (date > contractEnd) {
    return `${prefix}: ${fieldName} cannot be after Contract Term End Date`;
  }
  return null;
};

const stripUiOnlyTierFields = item => {
  if (!Array.isArray(item.revenueSplits)) return;

  item.revenueSplits.forEach(section => {
    if (
      section.guaranteeType === GUARANTEE_TYPE.STAGGERED &&
      Array.isArray(section.guaranteeTiers)
    ) {
      section.guaranteeTiers = section.guaranteeTiers.map(
        ({ isRevshare, ...tier }) => tier
      );
    }
  });
};

export const saveItem = saveItemInternal(
  modifyContractTerms,
  item => {
    // Validate contract term dates
    if (!item.startDate) return 'Contract Term: Start Date is required';
    if (!item.endDate) return 'Contract Term: End Date is required';
    if (item.startDate >= item.endDate) {
      return 'Contract Term: Start Date must be before End Date';
    }
    if (!item.paymentTerms) return 'Contract Term: Payment Terms are required';

    // Validate fixed fees
    if (Array.isArray(item.fixedFees)) {
      for (let i = 0; i < item.fixedFees.length; i++) {
        const fee = item.fixedFees[i];
        const prefix = `Fixed Fee ${i + 1}`;

        if (!fee.fixedFee) return `${prefix}: Fixed Fee is required`;
        if (!fee.startDate) return `${prefix}: Start Date is required`;
        if (!fee.amortizationEndDate)
          return `${prefix}: Amortization End Date is required`;
        if (!fee.implementationType)
          return `${prefix}: Implementation Type is required`;
        if (!fee.placementExperienceId)
          return `${prefix}: Placement Experience is required`;
        if (!fee.feePerMonth)
          return `${prefix}: Fee Per Month was not calculated, check your inputs.`;
        if (fee.feesType === 'variable') {
          if (!fee.contingentOnSessions)
            return `${prefix}: Sessions Per is required for Variable fees type`;
          if (!fee.timePeriod)
            return `${prefix}: Time Period is required for Variable fees type`;
        }
        const startDateError = validateDateInRange(
          fee.startDate,
          item.startDate,
          item.endDate,
          prefix,
          'Start Date'
        );
        if (startDateError) return startDateError;

        if (fee.amortizationEndDate) {
          if (fee.startDate > fee.amortizationEndDate) {
            return `${prefix}: Start Date must be on or before Amortization End Date`;
          }
          const endDateError = validateDateInRange(
            fee.amortizationEndDate,
            item.startDate,
            item.endDate,
            prefix,
            'Amortization End Date'
          );
          if (endDateError) return endDateError;
        }
      }
    }

    // Validate revenue splits
    if (Array.isArray(item.revenueSplits)) {
      for (let i = 0; i < item.revenueSplits.length; i++) {
        const section = item.revenueSplits[i];
        const prefix = `Revenue Split ${i + 1}`;

        if (
          !section.useAllAdUnitConfigs &&
          section.adUnitConfigIds.length === 0
        )
          return `${prefix}: At least one Ad Unit Configuration must be selected if 'All' is not selected`;

        if (section.usePartnerDataField && !section.partnerDataField)
          return `${prefix}: Partner Data Field is required if Contingent on Data is enabled`;

        // These fields only drive the Flat guarantee mechanism - a staggered split's
        // guarantee/revshare comes entirely from its guaranteeTiers, validated below.
        if (section.guaranteeType !== GUARANTEE_TYPE.STAGGERED) {
          if (hasValue(section.partnerPercentage)) {
            const partnerError = validateRevSharePercentage(
              Number(section.partnerPercentage) * 100
            );
            if (partnerError)
              return `${prefix}: Partner Percentage - ${partnerError}`;
          }
          if (hasValue(section.platformPercentage)) {
            const platformError = validateRevSharePercentage(
              Number(section.platformPercentage) * 100
            );
            if (platformError)
              return `${prefix}: Platform Percentage - ${platformError}`;
          }

          if (section.useCostPerSessionOverRevShareUntilSessionReached) {
            // When enabled, both partnerPercentage AND costPerSession are required
            if (!section.partnerPercentage)
              return `${prefix}: Partner Percentage is required when Cost Per Session Over Rev Share is enabled`;
            if (!section.costPerSession)
              return `${prefix}: Cost Per Session is required when Cost Per Session Over Rev Share is enabled`;
            if (!section.contingentOnSessionsCostPerSession)
              return `${prefix}: Sessions Per (Cost Per Session) is required when Cost Per Session Over Rev Share is enabled`;
            if (!section.timePeriodCostPerSession)
              return `${prefix}: Time Period (Cost Per Session) is required when Cost Per Session Over Rev Share is enabled`;
          } else {
            // Default: partnerPercentage OR costPerSession should be configured
            if (!section.partnerPercentage && !section.costPerSession)
              return `${prefix}: Either Partner Percentage or Cost Per Session is required`;
          }
        }

        // Validate contingent on sessions if enabled
        if (section.useContingentOnSessions && !section.contingentOnSessions)
          return `${prefix}: Sessions Per is required if Contingent on Sessions is enabled`;
        if (section.useContingentOnSessions && !section.timePeriod)
          return `${prefix}: Time Period is required if Contingent on Sessions is enabled`;

        if (section.guaranteeType === GUARANTEE_TYPE.STAGGERED) {
          const tiers = section.guaranteeTiers || [];

          if (tiers.length === 0)
            return `${prefix}: At least one guarantee tier is required when Guarantee Type is Staggered`;

          const { tierIssues, listIssues } = getGuaranteeTierIssues(tiers);
          if (listIssues.length > 0) return `${prefix}: ${listIssues[0]}`;
          if (tierIssues.length > 0)
            return `${prefix}, Tier ${tierIssues[0].index + 1}: ${tierIssues[0].message}`;

          for (let j = 0; j < tiers.length; j++) {
            const tier = tiers[j];
            const tierPrefix = `${prefix}, Tier ${j + 1}`;
            const hasLowerBound =
              tier.lowerBound !== '' && tier.lowerBound != null;
            const hasRevshareValue =
              tier.revshare !== '' && tier.revshare != null;
            const hasRpsGuarantee =
              tier.rpsGuarantee !== '' && tier.rpsGuarantee != null;

            if (!hasLowerBound) return `${tierPrefix}: Lower Bound is required`;

            if (isRevshareTier(tier)) {
              if (!hasRevshareValue)
                return `${tierPrefix}: Revshare (%) is required`;
            } else if (!hasRpsGuarantee) {
              return `${tierPrefix}: An RPS guarantee is required unless this is the revshare tier`;
            }
          }
        }
      }
    }

    // Validate referral fees
    if (Array.isArray(item.referralFees)) {
      for (let i = 0; i < item.referralFees.length; i++) {
        const fee = item.referralFees[i];
        const prefix = `Referral Fee ${i + 1}`;

        if (!fee.partnerId) return `${prefix}: Partner is required`;
        if (fee.amountType === 'dollar' && !fee.amount)
          return `${prefix}: Amount is required`;
        if (fee.amountType === 'percent' && !fee.amount)
          return `${prefix}: Percent is required`;
        if (!fee.startDate) {
          return `${prefix}: Start Date is required`;
        }
        if (!fee.endDate) {
          return `${prefix}: End Date is required`;
        }
        if (!fee.paymentTerms) return `${prefix}: Payment Terms are required`;

        if (fee.startDate && fee.endDate) {
          if (fee.startDate >= fee.endDate) {
            return `${prefix}: Start Date must be before End Date`;
          }

          const startDateError = validateDateInRange(
            fee.startDate,
            item.startDate,
            item.endDate,
            prefix,
            'Start Date'
          );
          if (startDateError) return startDateError;

          const endDateError = validateDateInRange(
            fee.endDate,
            item.startDate,
            item.endDate,
            prefix,
            'End Date'
          );
          if (endDateError) return endDateError;
        }
      }
    }
  },
  stripUiOnlyTierFields
);
